<template>
  <div class="flex justify-between px-6 py-4.5 title-menu">
    <!-- 左侧功能 -->
    <div class="flex items-center gap-4">
      <!-- macOS 使用原生红绿灯，这里只负责画出与按钮同高的视觉底座。 -->
      <Button
        v-if="showMacNativeWindowControls"
        as="div"
        variant="fill"
        color="glass"
        size="lg"
        shape="corner"
        class="mac-traffic-light-slot"
        aria-hidden="true"
      />
      <HomeButton class="can-drag" :active-tab="activeTab" @handle-tab="handleTab" />
      <Button
        v-for="item in leftTabs"
        :key="item.key"
        class="can-drag"
        variant="fill"
        color="glass"
        size="lg"
        shape="corner"
        :active="activeTab === item.key"
        @click="handleTab(item.key)"
        ><Icon :icon="item.icon"></Icon> {{ item.label }}
      </Button>
    </div>
    <!-- 右侧功能 -->
    <div class="flex items-center gap-4">
      <NewVersionButton v-if="isElectron" class="can-drag" />

      <!-- 旧版入口 -->
      <Button
        v-if="!isElectron"
        variant="fill"
        color="glass"
        size="icon-lg"
        shape="corner"
        class="can-drag"
        @click="handleToOldVersion"
      >
        <Icon icon="Ai-Fill-icon" />
        <span>{{ t('home.oldVersion') }}</span>
      </Button>

      <!-- 用户功能，图标需替换 -->
      <AvatarButton class="can-drag" />

      <!-- <ThemeButton /> -->

      <Button
        variant="fill"
        color="glass"
        size="icon-lg"
        shape="corner"
        :active="activeTab === 'settings'"
        class="can-drag"
        @click="handleTab('settings')"
      >
        <Icon icon="Status-Wireframe-Setting"></Icon>
      </Button>

      <!-- 电脑最小化键和关闭键,只有 Windows 客户端才显示，macOS 使用系统原生按钮。 -->
      <template v-if="showWindowsWindowControls">
        <Button
          variant="fill"
          color="glass"
          size="icon-lg"
          shape="corner"
          class="can-drag"
          @click="handleCollapse"
        >
          <Icon icon="Status-Wireframe-Collapse"></Icon>
        </Button>
        <Button
          variant="fill"
          color="glass"
          size="icon-lg"
          shape="corner"
          class="can-drag"
          @click="handleClose"
        >
          <Icon icon="Status-Wireframe-Close"></Icon>
        </Button>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed } from 'vue'
  import { storeToRefs } from 'pinia'
  import { Button, Icon } from '@rapoo/element'
  import AvatarButton from '@/components/user/AvatarButton.vue'
  import HomeButton from './components/HomeButton.vue'
  import NewVersionButton from './components/settings/newVersionButton.vue'
  import { useRoute, useRouter } from 'vue-router'

  type ItemKey = 'home' | 'configurations' | 'settings' | ''

  interface TabItem {
    label: string
    key: ItemKey
    icon: string
  }

  import { isElectron as isElectronEnv } from '@/utils/env'
  import { isMacOS, isWindows } from '@/utils/ua'
  const isElectron = isElectronEnv()
  const showWindowsWindowControls = isElectron && isWindows
  const showMacNativeWindowControls = isElectron && isMacOS

  import { hasConfigCapability, useConfigName } from '@/composables/useConfigName'
  import { useConfigurationStore, useDeviceStore, useLocaleStore } from '@/stores'
  const configStore = useConfigurationStore()
  const { currentConfig } = storeToRefs(configStore)
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  const { t } = useLocaleStore()

  const leftTabs = computed<TabItem[]>(() => {
    let configLabel = t('home.myConfig')
    const device = currentDevice.value
    const model = device?.manifest.model || ''
    if (hasConfigCapability(device) && currentConfig.value) {
      const name = useConfigName(currentConfig.value, device)
      configLabel = `${t('home.myConfig')}：${model}/${name}`
    } else if (hasConfigCapability(device) && model) {
      configLabel = `${t('home.myConfig')}：${model}`
    }

    const tabs = [
      {
        label: configLabel,
        key: 'configurations',
        icon: 'Status-Fill-Configuration',
      },
    ] as TabItem[]
    return tabs
  })

  const route = useRoute()
  const router = useRouter()

  const activeTab = computed<ItemKey>(() => {
    if (route.name === 'home') return 'home'
    if (route.name === 'headerConsole') {
      return (route.query.com as ItemKey) || ''
    }
    return ''
  })

  const handleTab = (key: ItemKey) => {
    if (key === 'home') {
      router.push({ name: 'home' })
      return
    }
    router.push({ name: 'headerConsole', query: { com: key } })
  }

  const handleCollapse = () => {
    window.electronAPI?.minimizeWindow()
  }

  const handleClose = () => {
    window.electronAPI?.closeWindow()
  }

  const handleToOldVersion = () => {
    window.location.href = 'https://hub1.rapoo.cn'
  }
</script>

<style lang="scss" scoped>
  .title-menu {
    app-region: drag;
    -webkit-app-region: drag;

    .mac-traffic-light-slot {
      flex: 0 0 7rem;
      height: 3rem;
      pointer-events: none;
    }

    .can-drag {
      app-region: no-drag;
      -webkit-app-region: no-drag;
    }
  }
</style>
